import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Home, Shield, Award, FileText, Phone, Calendar, CheckCircle2, Star, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../components/Header';
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';
import { leadFormSchema } from '../lib/validations';
import { submitLead } from '../lib/api';

const breadcrumbItems = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '/services' },
  { label: 'Sell Your Home', href: '/sell-your-home', current: true }
];

const timelineOptions = [
  'ASAP (0-7 days)',
  '1-2 weeks',
  '30 days',
  '2-3 months',
  'Just exploring options'
];

const conditionOptions = [
  'Excellent \u2013 move-in ready',
  'Good \u2013 minor cosmetic wear',
  'Fair \u2013 dated, needs some work',
  'Needs work \u2013 multiple repairs needed',
  'Major repairs / severe issues'
];

const reasonOptions = [
  'Facing foreclosure / behind on payments',
  'Inherited / probate',
  'Landlord tired of tenants',
  'Relocating / job change',
  'Downsizing / upsizing',
  'Just testing the market'
];

const occupancyOptions = [
  'Vacant',
  'Owner-occupied',
  'Tenant-occupied'
];

const mortgageOptions = [
  'Paid off / no mortgage',
  'Small remaining balance',
  'Significant remaining balance',
  'Behind on payments',
  'Prefer not to say'
];

const contactTimeOptions = [
  'Morning',
  'Afternoon',
  'Evening'
];

const testimonials = [
  {
    text: "Many homeowners come to us with inherited properties that need repairs or updates. A common theme we hear is how helpful it is to sell the home as-is and avoid the long listing process. Families often tell us the fast timeline and simple paperwork bring a sense of relief during an already stressful time.",
    outcome: "Quick, as-is sale with no repairs required.",
    location: "Los Angeles County"
  },
  {
    text: "Homeowners dealing with financial pressure frequently mention how much they appreciate getting a clear, fair offer without added fees or surprises. Many say that having a direct, responsive team to guide them through the process helps them move forward with confidence and peace of mind.",
    outcome: "Immediate offer and flexible closing options.",
    location: "Koreatown, Los Angeles"
  },
  {
    text: "Owners with challenging rental situations often highlight how valuable it is that Pacific Crest purchases properties with tenants in place. We routinely hear that being able to sell without repairs, vacancy, or hassles makes the experience noticeably easier and far less stressful.",
    outcome: "Tenant-occupied property purchased as-is, no eviction required.",
    location: "Silver Lake, Los Angeles"
  }
];

const offerProcessSteps = [
  { step: 1, icon: <FileText className="w-6 h-6" />, title: 'Tell Us About Your Property', description: 'Fill out our simple form with details about your home and situation. Takes less than 3 minutes.' },
  { step: 2, icon: <Phone className="w-6 h-6" />, title: 'Get Your Offer', description: "We'll evaluate your property and present you with a fair, no-obligation offer." },
  { step: 3, icon: <Calendar className="w-6 h-6" />, title: 'Close on Your Timeline', description: 'Choose your closing date. We handle everything and close on your terms.' }
];

const SellYourHomePage = () => {
  const form = useForm({
    resolver: zodResolver(leadFormSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      property_address: '',
      sms_consent: false,
    },
  });

  const [enrichment, setEnrichment] = useState({
    timeline_to_sell: '',
    condition: '',
    reason_for_selling: '',
    occupancy_status: '',
    mortgage_situation: '',
    best_time_to_contact: '',
  });

  const [showThankYou, setShowThankYou] = useState(false);

  const handleEnrichmentChange = (field, value) => {
    setEnrichment((prev) => ({ ...prev, [field]: value }));
  };

  const onSubmit = async (validatedValues) => {
    try {
      await submitLead({
        ...validatedValues,
        lead_type: 'sell',
        lead_source: 'offer',
        ...enrichment,
      });
      setShowThankYou(true);
      toast.success('Form submitted successfully!');
      form.reset();
      setEnrichment({
        timeline_to_sell: '',
        condition: '',
        reason_for_selling: '',
        occupancy_status: '',
        mortgage_situation: '',
        best_time_to_contact: '',
      });
    } catch (error) {
      if (error.response?.status === 422) {
        const serverErrors = error.response.data.errors;
        if (Array.isArray(serverErrors)) {
          serverErrors.forEach(({ field, message }) => {
            if (form.getValues(field) !== undefined) {
              form.setError(field, { type: 'server', message });
            }
          });
        }
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    }
  };

  const scrollToForm = (e) => {
    if (e) e.preventDefault();
    const formSection = document.getElementById('get-offer');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => {
        const firstInput = document.getElementById('first_name');
        if (firstInput) firstInput.focus();
      }, 600);
    }
  };

  // Auto-scroll to form when arriving with #get-offer hash (from external links)
  React.useEffect(() => {
    if (window.location.hash === '#get-offer') {
      setTimeout(() => scrollToForm(), 300);
    }
  }, []);

  const inputBaseClass = "w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#D6A94F] focus:border-transparent transition-colors";
  const labelClass = "block text-sm font-semibold mb-1.5";
  const selectClass = "w-full px-4 py-3 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#D6A94F] focus:border-transparent transition-colors appearance-none";

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1F3B60' }}>
      <Helmet>
        <title>Sell Your Home | Pacific Crest Properties — Los Angeles</title>
        <meta name="description" content="Sell your Los Angeles home quickly and easily. Pacific Crest Properties buys properties as-is with no repairs needed, no agent fees, and flexible closing timelines." />
        <meta name="keywords" content="sell house fast Los Angeles, as-is home buyers LA, cash offer home LA, quick home sale Los Angeles" />
        <link rel="canonical" href="https://offer.pacificcrestprop.com" />
        <meta property="og:title" content="Sell Your Home | Pacific Crest Properties" />
        <meta property="og:description" content="Get a fast, fair offer for your Los Angeles property. No repairs, no agents, no hassle." />
        <meta property="og:url" content="https://offer.pacificcrestprop.com" />
      </Helmet>

      <Header />
      <div className="pt-20">
        <Breadcrumb items={breadcrumbItems} variant="dark" />
      </div>

      {/* Hero Section */}
      <section className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block font-semibold text-xs tracking-[0.2em] uppercase mb-4 px-4 py-1.5 rounded-full" style={{ color: '#D6A94F', backgroundColor: 'rgba(214, 169, 79, 0.1)', border: '1px solid rgba(214, 169, 79, 0.2)' }}>
            Property Offers
          </span>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
            Sell Your Home Fast<br />in Los Angeles!
          </h1>
          <p className="text-2xl sm:text-3xl font-semibold mb-4" style={{ color: '#D6A94F' }}>
            No Repairs. No Agents. No Stress.
          </p>
          <div className="divider-gold mx-auto mb-8" />
          <p className="text-xl text-white/70 leading-relaxed max-w-3xl mx-auto mb-10">
            Pacific Crest Properties gives you a fair, transparent offer so you can move on your timeline.
          </p>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-8 mb-12">
            <div className="flex items-center space-x-2 text-white">
              <Shield className="w-5 h-5" style={{ color: '#D6A94F' }} />
              <span className="text-lg">Local LA Investors</span>
            </div>
            <div className="flex items-center space-x-2 text-white">
              <Home className="w-5 h-5" style={{ color: '#D6A94F' }} />
              <span className="text-lg">As-Is Offers</span>
            </div>
            <div className="flex items-center space-x-2 text-white">
              <Award className="w-5 h-5" style={{ color: '#D6A94F' }} />
              <span className="text-lg">No Fees or Commissions</span>
            </div>
          </div>

          <a
            href="#get-offer"
            onClick={scrollToForm}
            className="btn-gold inline-flex items-center space-x-2 text-lg group animate-pulse-glow"
          >
            <span>Get My Offer Now</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F4F1E9' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display font-bold text-3xl sm:text-4xl mb-4" style={{ color: '#1F3B60' }}>
              How It Works
            </h2>
            <p className="text-lg mb-4" style={{ color: '#3A5A85' }}>
              Three simple steps to selling your home
            </p>
            <div className="divider-gold mx-auto" />
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {offerProcessSteps.map((item) => (
              <div key={item.step} className="bg-white rounded-2xl p-6 shadow-md text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #D6A94F, #E4C078)' }}>
                  <div style={{ color: '#1F3B60' }}>{item.icon}</div>
                </div>
                <div className="step-circle mx-auto mb-4" style={{ width: '36px', height: '36px', fontSize: '1rem' }}>
                  {item.step}
                </div>
                <h3 className="font-display font-bold text-xl mb-2" style={{ color: '#1F3B60' }}>{item.title}</h3>
                <p style={{ color: '#3A5A85' }}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="get-offer" className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#1F3B60' }}>
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-white mb-4">
              Get Your Offer Today
            </h2>
            <p className="text-lg text-white/70 max-w-xl mx-auto mb-4">
              Fill out the form below and we'll contact you with a fair offer
            </p>
            <div className="divider-gold mx-auto" />
          </div>

          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg">
            {showThankYou ? (
              <div className="text-center py-8">
                <div className="mb-6 flex justify-center">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1F3B60, #D6A94F)' }}>
                    <CheckCircle2 className="w-12 h-12 text-white" />
                  </div>
                </div>
                <h3 className="font-display font-bold text-3xl mb-4" style={{ color: '#1F3B60' }}>
                  We've Got Your Information!
                </h3>
                <p className="text-lg mb-8" style={{ color: '#3A5A85' }}>
                  Thank you for your interest! We'll review your information and reach out soon to discuss how we can help.
                </p>
                <button
                  onClick={() => { form.reset(); setShowThankYou(false); }}
                  className="btn-gold inline-flex items-center space-x-2"
                >
                  <span>Submit Another Property</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                {/* Row 1: First Name + Last Name */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass} style={{ color: '#1F3B60' }}>First Name</label>
                    <input
                      type="text"
                      id="first_name"
                      {...form.register('first_name')}
                      placeholder="First name"
                      className={`${inputBaseClass} ${form.formState.errors.first_name ? 'border-red-500' : ''}`}
                    />
                    {form.formState.errors.first_name && (
                      <p className="text-red-500 text-xs mt-1">{form.formState.errors.first_name.message}</p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass} style={{ color: '#1F3B60' }}>Last Name</label>
                    <input
                      type="text"
                      {...form.register('last_name')}
                      placeholder="Last name"
                      className={`${inputBaseClass} ${form.formState.errors.last_name ? 'border-red-500' : ''}`}
                    />
                    {form.formState.errors.last_name && (
                      <p className="text-red-500 text-xs mt-1">{form.formState.errors.last_name.message}</p>
                    )}
                  </div>
                </div>

                {/* Row 2: Phone + Email */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass} style={{ color: '#1F3B60' }}>Phone Number</label>
                    <input
                      type="tel"
                      {...form.register('phone_number')}
                      placeholder="(555) 123-4567"
                      className={`${inputBaseClass} ${form.formState.errors.phone_number ? 'border-red-500' : ''}`}
                    />
                    {form.formState.errors.phone_number && (
                      <p className="text-red-500 text-xs mt-1">{form.formState.errors.phone_number.message}</p>
                    )}
                  </div>
                  <div>
                    <label className={labelClass} style={{ color: '#1F3B60' }}>Email</label>
                    <input
                      type="email"
                      {...form.register('email')}
                      placeholder="you@example.com"
                      className={`${inputBaseClass} ${form.formState.errors.email ? 'border-red-500' : ''}`}
                    />
                    {form.formState.errors.email && (
                      <p className="text-red-500 text-xs mt-1">{form.formState.errors.email.message}</p>
                    )}
                  </div>
                </div>

                {/* Row 3: Property Address */}
                <div>
                  <label className={labelClass} style={{ color: '#1F3B60' }}>Property Address</label>
                  <input
                    type="text"
                    {...form.register('property_address')}
                    placeholder="123 Main St, Los Angeles, CA"
                    className={`${inputBaseClass} ${form.formState.errors.property_address ? 'border-red-500' : ''}`}
                  />
                  {form.formState.errors.property_address && (
                    <p className="text-red-500 text-xs mt-1">{form.formState.errors.property_address.message}</p>
                  )}
                </div>

                {/* Row 4: Timeline + Condition */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass} style={{ color: '#1F3B60' }}>Timeline to Sell</label>
                    <div className="relative">
                      <select
                        value={enrichment.timeline_to_sell}
                        onChange={(e) => handleEnrichmentChange('timeline_to_sell', e.target.value)}
                        className={selectClass}
                        style={{ color: enrichment.timeline_to_sell ? '#1F3B60' : '#9CA3AF' }}
                      >
                        <option value="" disabled>Select timeline</option>
                        {timelineOptions.map(opt => (
                          <option key={opt} value={opt} style={{ color: '#1F3B60' }}>{opt}</option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass} style={{ color: '#1F3B60' }}>Property Condition</label>
                    <div className="relative">
                      <select
                        value={enrichment.condition}
                        onChange={(e) => handleEnrichmentChange('condition', e.target.value)}
                        className={selectClass}
                        style={{ color: enrichment.condition ? '#1F3B60' : '#9CA3AF' }}
                      >
                        <option value="" disabled>Select condition</option>
                        {conditionOptions.map(opt => (
                          <option key={opt} value={opt} style={{ color: '#1F3B60' }}>{opt}</option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 5: Reason + Occupancy */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass} style={{ color: '#1F3B60' }}>Reason for Selling</label>
                    <div className="relative">
                      <select
                        value={enrichment.reason_for_selling}
                        onChange={(e) => handleEnrichmentChange('reason_for_selling', e.target.value)}
                        className={selectClass}
                        style={{ color: enrichment.reason_for_selling ? '#1F3B60' : '#9CA3AF' }}
                      >
                        <option value="" disabled>Select reason</option>
                        {reasonOptions.map(opt => (
                          <option key={opt} value={opt} style={{ color: '#1F3B60' }}>{opt}</option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass} style={{ color: '#1F3B60' }}>Occupancy Status</label>
                    <div className="relative">
                      <select
                        value={enrichment.occupancy_status}
                        onChange={(e) => handleEnrichmentChange('occupancy_status', e.target.value)}
                        className={selectClass}
                        style={{ color: enrichment.occupancy_status ? '#1F3B60' : '#9CA3AF' }}
                      >
                        <option value="" disabled>Select status</option>
                        {occupancyOptions.map(opt => (
                          <option key={opt} value={opt} style={{ color: '#1F3B60' }}>{opt}</option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 6: Mortgage + Best Time */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass} style={{ color: '#1F3B60' }}>Mortgage Situation</label>
                    <div className="relative">
                      <select
                        value={enrichment.mortgage_situation}
                        onChange={(e) => handleEnrichmentChange('mortgage_situation', e.target.value)}
                        className={selectClass}
                        style={{ color: enrichment.mortgage_situation ? '#1F3B60' : '#9CA3AF' }}
                      >
                        <option value="" disabled>Select mortgage status</option>
                        {mortgageOptions.map(opt => (
                          <option key={opt} value={opt} style={{ color: '#1F3B60' }}>{opt}</option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass} style={{ color: '#1F3B60' }}>Best Time to Contact</label>
                    <div className="relative">
                      <select
                        value={enrichment.best_time_to_contact}
                        onChange={(e) => handleEnrichmentChange('best_time_to_contact', e.target.value)}
                        className={selectClass}
                        style={{ color: enrichment.best_time_to_contact ? '#1F3B60' : '#9CA3AF' }}
                      >
                        <option value="" disabled>Select time</option>
                        {contactTimeOptions.map(opt => (
                          <option key={opt} value={opt} style={{ color: '#1F3B60' }}>{opt}</option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SMS Consent */}
                <div className="flex items-start space-x-3 pt-2">
                  <input
                    type="checkbox"
                    id="sms-consent"
                    checked={form.watch('sms_consent')}
                    onChange={(e) => form.setValue('sms_consent', e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 focus:ring-2 focus:ring-[#D6A94F]"
                    style={{ accentColor: '#D6A94F' }}
                  />
                  <label htmlFor="sms-consent" className="text-xs leading-relaxed" style={{ color: '#3A5A85' }}>
                    By checking this box, I agree to receive text messages from Pacific Crest Properties
                    regarding my property inquiry, appointment scheduling, and related real estate services.
                    Consent is not a condition of purchase. Message frequency varies. Message and data rates may apply.
                    You may opt out at any time by replying STOP. For assistance, reply HELP.
                    View our{' '}
                    <Link to="/privacy-policy" className="underline font-medium" style={{ color: '#D6A94F' }}>
                      Privacy Policy
                    </Link>{' '}
                    and{' '}
                    <Link to="/terms-and-conditions" className="underline font-medium" style={{ color: '#D6A94F' }}>
                      Terms &amp; Conditions
                    </Link>.
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={form.formState.isSubmitting}
                  className="btn-gold w-full flex items-center justify-center space-x-2 text-lg"
                  style={{ opacity: form.formState.isSubmitting ? 0.7 : 1 }}
                >
                  {form.formState.isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <span>Get My Offer Now</span>
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F4F1E9' }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display font-bold text-3xl sm:text-4xl mb-4" style={{ color: '#1F3B60' }}>
              What Homeowners Experience With Pacific Crest
            </h2>
            <p className="text-lg mb-4" style={{ color: '#3A5A85' }}>
              Insights and common outcomes from homeowners we've helped across Los Angeles.
            </p>
            <div className="divider-gold mx-auto" />
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-md flex flex-col">
                {/* Star Rating */}
                <div className="flex space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current" style={{ color: '#D6A94F' }} />
                  ))}
                </div>

                <p className="text-sm leading-relaxed mb-4 flex-grow" style={{ color: '#3A5A85' }}>
                  "{item.text}"
                </p>

                <div className="border-t pt-4 mt-auto" style={{ borderColor: 'rgba(214, 169, 79, 0.2)' }}>
                  <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#D6A94F' }}>
                    Typical Outcome
                  </p>
                  <p className="text-sm font-medium mb-2" style={{ color: '#1F3B60' }}>
                    {item.outcome}
                  </p>
                  <p className="text-xs" style={{ color: '#3A5A85' }}>
                    {item.location}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#1F3B60' }}>
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-lg text-white/70 mb-4">
            Trusted escrow and title services provided by
          </p>
          <p className="text-2xl font-bold text-white mb-1">
            Culture Escrow
          </p>
          <p className="text-white/60">
            San Marino, California
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F4F1E9' }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display font-bold text-3xl sm:text-4xl mb-4" style={{ color: '#1F3B60' }}>
            Ready to Get Started?
          </h2>
          <p className="text-lg mb-8" style={{ color: '#3A5A85' }}>
            Submit your property details and receive a personalized offer. No obligation, no pressure.
          </p>
          <a
            href="#get-offer"
            onClick={scrollToForm}
            className="btn-gold inline-flex items-center space-x-2 text-lg group"
          >
            <Home className="w-5 h-5" />
            <span>Get Your Offer</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/services" className="text-sm font-medium transition-colors" style={{ color: '#3A5A85' }}>
              &larr; Back to Services
            </Link>
            <Link
              to="/renovation"
              className="text-sm font-medium transition-colors"
              style={{ color: '#D6A94F' }}
            >
              Explore Renovation Services &rarr;
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SellYourHomePage;
